import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Search, UserPlus, UserX, UserSearch, UserCheck, CalendarDays, ChevronDown, ChevronUp, Users,
} from 'lucide-react';
import { Prospect, ProspectInput } from '../../types';
import {
    useProspectsQuery,
    useCreateProspectMutation,
    useDeactivateProspectMutation,
} from '../../services/hooks/useProspects';
import ConfirmationModal from '../common/ConfirmationModal';
import ProspectFormModal from './ProspectFormModal';
import { formatDate } from '../../utils/utils';

// D2 — listado de personas que vinieron a probar una clase. Junto a Clientes,
// SEPA, Otros pagos y Reportes, que es donde la PO esperaba encontrarlo.
//
// Sigue deliberadamente la misma estructura que `AllClientList`: panel de
// estadísticas, botonera, buscador con filtros y tabla. Es la pantalla hermana y
// se usa igual, así que no debe parecer otra aplicación.
//
// No son clientes: no tienen plaza, ni portal, ni DNI. Las clases a las que
// fueron las calcula el backend consultando los eventos.

type FilterStatus = 'all' | 'pending' | 'converted';

const ProspectsList: React.FC = () => {
    const navigate = useNavigate();
    const { tenantCode } = useParams<{ tenantCode: string }>();

    const [verBajas, setVerBajas] = useState(false);
    const { data: prospects = [], isLoading, isError } = useProspectsQuery(verBajas);
    const { mutateAsync: crearProspecto, isPending: creando } = useCreateProspectMutation();
    const { mutateAsync: darDeBaja } = useDeactivateProspectMutation();

    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
    const [expandido, setExpandido] = useState<string | null>(null);
    const [mostrarAlta, setMostrarAlta] = useState(false);
    const [aDarDeBaja, setADarDeBaja] = useState<Prospect | null>(null);

    const stats = useMemo(() => ({
        total: prospects.length,
        pending: prospects.filter((p) => !p.convertedClientId && p.active !== false).length,
        converted: prospects.filter((p) => p.convertedClientId).length,
        clases: prospects.reduce((sum, p) => sum + (p.attendance?.length ?? 0), 0),
    }), [prospects]);

    const filtrados = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        return prospects.filter((p) => {
            const searchMatch = !q
                || p.name.toLowerCase().includes(q)
                || p.email.toLowerCase().includes(q)
                || p.phone.includes(q);

            const statusMatch = filterStatus === 'all'
                || (filterStatus === 'converted' ? !!p.convertedClientId : !p.convertedClientId);

            return searchMatch && statusMatch;
        });
    }, [prospects, searchTerm, filterStatus]);

    const handleCrear = async (prospecto: ProspectInput) => {
        await crearProspecto(prospecto);
        setMostrarAlta(false);
    };

    // Convertir = darla de alta como cliente por el flujo normal (buscar plazas →
    // formulario de inscripción), con sus datos ya puestos. El prospecto viaja en
    // el state por esa cadena; el enlace con la ficha de cliente lo hace el
    // backend al terminar la inscripción, no el navegador.
    const handleConvertir = (prospect: Prospect) => {
        navigate(`/${tenantCode}/calendario`, {
            state: {
                prospect: {
                    id: prospect._id,
                    name: prospect.name,
                    phone: prospect.phone,
                    email: prospect.email,
                },
            },
        });
    };

    if (isLoading) {
        return <div className="bg-white rounded-lg shadow p-6 text-gray-500">Cargando personas de prueba…</div>;
    }
    if (isError) {
        return <div className="bg-white rounded-lg shadow p-6 text-red-600">No se pudieron cargar las personas de prueba.</div>;
    }

    return (
        <div className="bg-white rounded-lg shadow">
            {/* Panel de estadísticas */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4 border-b bg-blue-50">
                <div className="flex items-center justify-center md:justify-start p-2 rounded-lg bg-white shadow-sm">
                    <UserSearch className="h-8 w-8 text-blue-500 mr-3" />
                    <div>
                        <div className="text-xs text-gray-500">Total</div>
                        <div className="text-xl font-bold">{stats.total}</div>
                    </div>
                </div>
                <div className="flex items-center justify-center md:justify-start p-2 rounded-lg bg-white shadow-sm">
                    <Users className="h-8 w-8 text-amber-500 mr-3" />
                    <div>
                        <div className="text-xs text-gray-500">Sin convertir</div>
                        <div className="text-xl font-bold">{stats.pending}</div>
                    </div>
                </div>
                <div className="flex items-center justify-center md:justify-start p-2 rounded-lg bg-white shadow-sm">
                    <UserCheck className="h-8 w-8 text-green-500 mr-3" />
                    <div>
                        <div className="text-xs text-gray-500">Ya son clientes</div>
                        <div className="text-xl font-bold">{stats.converted}</div>
                    </div>
                </div>
                <div className="flex items-center justify-center md:justify-start p-2 rounded-lg bg-white shadow-sm">
                    <CalendarDays className="h-8 w-8 text-purple-500 mr-3" />
                    <div>
                        <div className="text-xs text-gray-500">Clases de prueba</div>
                        <div className="text-xl font-bold">{stats.clases}</div>
                    </div>
                </div>
            </div>

            {/* Botones de acción */}
            <div className="flex justify-end gap-3 p-4">
                <button
                    onClick={() => setMostrarAlta(true)}
                    className="bg-brand-primary hover:brightness-90 text-white py-2 px-4 rounded flex items-center gap-2"
                >
                    <UserPlus className="h-5 w-5" />
                    Añadir
                </button>
            </div>

            {/* Panel de búsqueda y filtros */}
            <div className="p-4 border-b">
                <h2 className="text-lg font-semibold">Personas de prueba</h2>
                <p className="text-xs text-gray-500">
                    Quien viene a probar una clase sin ser cliente. No ocupan plaza fija, pero sí
                    sitio en la clase a la que van.
                </p>

                <div className="mt-2 flex flex-col md:flex-row md:flex-wrap gap-2">
                    <div className="relative flex-grow">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Buscar por nombre, teléfono o correo"
                            className="pl-10 pr-4 py-2 border rounded w-full"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="flex flex-wrap items-center gap-1">
                        <button
                            onClick={() => setFilterStatus('all')}
                            className={`flex items-center gap-1 px-3 py-2 rounded border ${filterStatus === 'all' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'}`}
                        >
                            <Users size={16} />
                            <span className="hidden sm:inline">Todas</span>
                        </button>
                        <button
                            onClick={() => setFilterStatus('pending')}
                            className={`flex items-center gap-1 px-3 py-2 rounded border ${filterStatus === 'pending' ? 'bg-amber-500 text-white' : 'bg-white text-gray-700'}`}
                        >
                            <UserSearch size={16} />
                            <span className="hidden sm:inline">Sin convertir</span>
                        </button>
                        <button
                            onClick={() => setFilterStatus('converted')}
                            className={`flex items-center gap-1 px-3 py-2 rounded border ${filterStatus === 'converted' ? 'bg-green-500 text-white' : 'bg-white text-gray-700'}`}
                        >
                            <UserCheck size={16} />
                            <span className="hidden sm:inline">Ya son clientes</span>
                        </button>
                        <label className="flex items-center gap-2 px-3 py-2 text-xs text-gray-600">
                            <input
                                type="checkbox"
                                checked={verBajas}
                                onChange={(e) => setVerBajas(e.target.checked)}
                                className="h-4 w-4 rounded border-gray-300"
                            />
                            Ver también las dadas de baja
                        </label>
                    </div>
                </div>
            </div>

            <div className="px-4 py-2 text-sm text-gray-500 border-b">
                <strong>Mostrando {filtrados.length}</strong> de {prospects.length} personas de prueba
                {searchTerm && <span> (filtradas por búsqueda)</span>}
            </div>

            {filtrados.length === 0 ? (
                <div className="p-8 text-center text-sm text-gray-500">
                    {prospects.length === 0
                        ? 'Todavía no hay nadie registrado como persona de prueba.'
                        : 'Ninguna persona coincide con la búsqueda.'}
                </div>
            ) : (
                <div className="w-full overflow-x-auto pb-16">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-2 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Nombre
                                </th>
                                <th scope="col" className="px-2 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Contacto
                                </th>
                                <th scope="col" className="px-2 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Clases de prueba
                                </th>
                                <th scope="col" className="px-2 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filtrados.map((p) => {
                                const abierto = expandido === p._id;
                                const clases = p.attendance ?? [];
                                return (
                                    <React.Fragment key={p._id}>
                                        <tr className="hover:bg-gray-50">
                                            <td className="px-2 py-4">
                                                <div className="flex items-center">
                                                    <UserSearch className="h-8 w-8 text-gray-400 shrink-0" />
                                                    <div className="ml-4 min-w-0">
                                                        <div className="text-sm font-medium text-gray-900">{p.name}</div>
                                                        {p.convertedClientId && (
                                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                                Ya es cliente
                                                            </span>
                                                        )}
                                                        {p.active === false && (
                                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-600">
                                                                De baja
                                                            </span>
                                                        )}
                                                        {p.notes && (
                                                            <div className="text-xs text-gray-400">{p.notes}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-2 py-4">
                                                <div className="text-sm text-gray-900">{p.phone}</div>
                                                <div className="text-sm text-gray-500 break-all">{p.email}</div>
                                            </td>
                                            <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-500">
                                                <button
                                                    onClick={() => setExpandido(abierto ? null : p._id)}
                                                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                                                >
                                                    <CalendarDays size={14} />
                                                    {clases.length} clase{clases.length === 1 ? '' : 's'}
                                                    {abierto ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                                </button>
                                            </td>
                                            <td className="px-2 py-4 whitespace-nowrap text-sm">
                                                {!p.convertedClientId && p.active !== false && (
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => handleConvertir(p)}
                                                            className="rounded bg-brand-primary px-2 py-1 text-xs text-white hover:brightness-90"
                                                        >
                                                            Convertir en cliente
                                                        </button>
                                                        <button
                                                            onClick={() => setADarDeBaja(p)}
                                                            title="Dar de baja"
                                                            className="rounded border border-gray-300 p-1 text-gray-600 hover:bg-gray-50"
                                                        >
                                                            <UserX size={14} />
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                        {abierto && (
                                            <tr className="bg-gray-50">
                                                <td colSpan={4} className="px-6 py-3">
                                                    {clases.length === 0 ? (
                                                        <p className="text-xs text-gray-500">
                                                            Todavía no se le ha apuntado a ninguna clase.
                                                        </p>
                                                    ) : (
                                                        <ul className="space-y-1">
                                                            {clases.map((c) => (
                                                                <li key={c.eventId} className="text-xs text-gray-700">
                                                                    <strong>{formatDate(c.fecha)}</strong> · {c.horaInicio}–{c.horaFin}
                                                                    {c.levelName ? ` · ${c.levelName}` : ''}
                                                                    {c.sala ? ` · Sala ${c.sala}` : ''}
                                                                    {c.cancelled && (
                                                                        <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">
                                                                            Clase cancelada
                                                                        </span>
                                                                    )}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {mostrarAlta && (
                <ProspectFormModal
                    onClose={() => setMostrarAlta(false)}
                    onSave={handleCrear}
                    isSaving={creando}
                />
            )}

            <ConfirmationModal
                isOpen={!!aDarDeBaja}
                confirmMessage={`¿Dar de baja a <strong>${aDarDeBaja?.name ?? ''}</strong>? Se la quitará de las clases a las que todavía no ha ido, y esas plazas se ofrecerán a la lista de espera. Las clases a las que ya fue se conservan.`}
                onConfirm={async () => {
                    if (!aDarDeBaja) return;
                    await darDeBaja(aDarDeBaja._id);
                    setADarDeBaja(null);
                }}
                onClose={() => setADarDeBaja(null)}
            />
        </div>
    );
};

export default ProspectsList;
